/// <amd-module name="Test/TestResultsGrid/Components/TestResultsGrid" />
import { CheckboxVisibility, ColumnActionsMode, IColumn, SelectionMode } from "OfficeFabric/DetailsList";
import * as React from "react";
import {
    ColumnIndices,
    ContributionSource,
    FeatureFlagConstants,
    IconDetails,
    IViewContextData,
    TestResultFlakyState,
    TestResultsGroupPivots,
    ViewContext,
    ViewContextStatus,
} from "Test/common-util/Common";
import { hasArtifactChanged } from "Test/common-util/Common.Utils";
import { ITestResultTreeData, TreeNodeType } from "Test/common-util/Contracts/ViewContracts";
import { FilterHelper } from "Test/common-util/FilterHelper";
import * as TcmPerfScenarios from "Test/common-util/TCMPerfScenarios";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { getFriendlyName, getIconDetails } from "Test/common-util/TestOutcomeUtils";
import { areFilterStatesEqual } from "Test/common-util/TestResults.Filtering.Common";
import { TestResultsListViewActionCreator } from "Test/TestResultsGrid/Actions/TestResultsGridActionsCreator";
import * as Resources from "Test/TestResultsGrid/Resources";
import { ITestResultsState, TestResultsStore } from "Test/TestResultsGrid/Store/TestResultsTreeStore";
import { FilterSettings } from "Test/TestResultsGrid/TestResultGridHelper";
import { getTFSPageData } from "Tfs/Platform/Page";
import { announce } from "VSS/Core/Util/Accessibility";
import { format, localeFormat } from "VSS/Core/Util/String";
import { FPSLinkComponent } from "VSS/Platform/Components/FPSLink";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation, routeUrl } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Icon } from "VSSUI/Icon";
import { Image } from "VSSUI/Image";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { ITreeItem, ITreeItemProvider, ITreeListItem, Tree, ITree } from "VSSUI/Tree";

export interface ITestResultsListProps extends IVssComponentProperties {
    artifactData: IViewContextData;
    actionCreator: TestResultsListViewActionCreator;
    store: TestResultsStore;
    showTestResultsGrid?: boolean;
    isNoConfigRuns?: boolean;
    isOnlyRunFetch?: boolean;
    isDetailsPanelOpened?: boolean;
}

export interface ITestResultsComponentState extends ITestResultsState {
    treeItemProvider: ITreeItemProvider<ITestResultTreeData>;
}

export class TestResultsTree extends VssComponent<ITestResultsListProps, ITestResultsComponentState, IVssComponentContext> {
    private static readonly c_runIdKey = "runId";
    private dontOpenDetailsPaneCssClass: string = "dont-open-detail-pane";
    private _performanceService: IVssPerformanceService;
    private _markedAsTTI: boolean;
    private _historyService: IVssHistoryService;
    private runId: string;
    private _broughtBackFocusToSelectedElement: boolean;
    private _treeRef: ITree<ITestResultTreeData>;
    private _lastSelectedItemOfGrid: Element | null;

    public constructor(props: ITestResultsListProps, context: IVssComponentContext, private _artifactData: IViewContextData) {
        super(props, context);

        this.state = {
            ...this.props.store.getState(),
            treeItemProvider: this.props.store.getTreeItemProvider(),
        };
        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._markedAsTTI = false;
        this._historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._broughtBackFocusToSelectedElement = true;
    }

    public componentDidUpdate(prevProps: ITestResultsListProps) {
        if (this.props.isOnlyRunFetch) {
            if (!this.props.artifactData.runId) {
                this.props.artifactData.runId = this.runId;
                this._closeTestResultGridPerfScenario();
                this.props.actionCreator.initialize(this.props.artifactData);
            }
            else {
                this._handleArtifactUpdate(prevProps);
            }
        }
        else {
            this._handleArtifactUpdate(prevProps);
        }

        // This will bring focus to tree when we close details panel.
        if(this.props.isDetailsPanelOpened == false && this._broughtBackFocusToSelectedElement == false) {
            this.setFocusToLastSelectedItems();
            this._broughtBackFocusToSelectedElement = true;
        }
    }

    private _handleArtifactUpdate(prevProps: ITestResultsListProps) {
        this._closeTestResultGridPerfScenario();
        if (hasArtifactChanged(this.props.artifactData, prevProps.artifactData)) {
            // re-initializing the grid if the status is changed.
            this.props.actionCreator.initialize(this.props.artifactData);
        }
    }

    public componentDidMount() {
        const getState = this._historyService.getState();
        this.runId = getState[TestResultsTree.c_runIdKey];
        if (this.props.isOnlyRunFetch) {
            this.props.artifactData.runId = this.runId;
        }
        this.props.store.addChangedListener(this._handleStoreChange);
        this.props.actionCreator.initialize(this.props.artifactData);
        this._closeTestResultGridPerfScenario();
    }

    public componentWillUnmount() {
        this.props.store.removeChangedListener(this._handleStoreChange);
        if (
            !this.state.endPerfMarker &&
            (this.props.artifactData.contributionSource === ContributionSource.Build ||
                this.props.artifactData.contributionSource === ContributionSource.Pipeline)
        ) {
            this._performanceService.endScenario(
                ViewContext[this.props.artifactData.viewContext],
                this.props.artifactData.contributionSource,
                true,
                undefined,
                { aborted: true, reason: "Component getting unmounted before marking the scenario.", state: this._getArtifactStatus() }
            );
        }
    }

    public render(): JSX.Element | null {
        // Returning null, parent will handle the loading scenario.
        if (!this.props.showTestResultsGrid) {
            return null;
        }

        if(this.props.isDetailsPanelOpened == true) {
            this._broughtBackFocusToSelectedElement = false;
        }

        const totalCountToBeFetched: number = this.state.hasMegaFetchStarted ? this.state.totalResultsCount : this.state.failedAbortedCount;
        if (this.state.loading) {
            return this._getLoadingSpinner();
        }

        if (this.state.errorMessage && this.state.errorMessage !== "") {
            return (
                <MessageBar className="test-results-grid-error-message-bar" severity={MessageBarSeverity.Error}>
                    {this.state.errorMessage}
                </MessageBar>
            );
        }

        if (!this.state.resultsFound) {
            const isInitialFilterState: boolean =
                areFilterStatesEqual(this.state.filterState, FilterHelper.getInitialFilterState()) && this.state.fetchedResultsCount === 0;

            return (
                <div className="flex-grow">
                    {this.state.showingResultsCount > 0 &&
                        this.state.showingResultsCount < totalCountToBeFetched && this.state.showLoading &&
                        this._getFilterMessageBar(this.state.showingResultsCount, totalCountToBeFetched)}
                    {!isInitialFilterState && this._getNoResultsFoundImage()}
                    {isInitialFilterState && this._getNoFailedResultsImage()}
                </div>
            );
        }

        return (
            //Making immediate parent div scrollable and data-is-scrollable=true to receive the scroll event from DetailList
            <React.Fragment>
                {this.state.warningMessage &&
                    this.state.warningMessage !== "" &&
                    this._getWarningMessageBar(this.state.warningMessage)}
                {this.state.showingResultsCount > 0 &&
                    this.state.showingResultsCount < totalCountToBeFetched && this.state.showLoading &&
                    this._getFilterMessageBar(this.state.showingResultsCount, totalCountToBeFetched)}
                <div className="flex-grow scroll-auto" data-is-scrollable={true}>
                    <Tree
                        className="results-tree"
                        columns={this._getColumns(this.state.columnToRender)}
                        treeItemProvider={this.state.treeItemProvider}
                        onRowDidMount={this._onRowDidMount}
                        selectionMode={SelectionMode.multiple}
                        onSelectedItemsChanged={this._onSelectedItemsChanged}
                        onItemInvoked={this._onCellClick}
                        checkboxVisibility={CheckboxVisibility.onHover}
                        ariaLabelForSelectionColumn={Resources.SelectAll}
                        checkButtonAriaLabel={Resources.SelectRow}
                        componentRef={this._getTreeRef}
                    />
                </div>
            </React.Fragment>
        );
    }

    // This is used to select the last selected element when we are coming from full view to half view of details panel.
    private setFocusToLastSelectedItems() {
        if(!!this.state.selectedTreeItems && this.state.selectedTreeItems.length == 1 && this._treeRef) {
            let selectedItemId = this.state.selectedTreeItems[0].groupId;
            requestAnimationFrame(() => this._treeRef.setSelectedItemsById([selectedItemId]));
            
            if(this._lastSelectedItemOfGrid && this._lastSelectedItemOfGrid.parentElement) {
                this._lastSelectedItemOfGrid.parentElement.scrollIntoView();
            }
        }
    }

    private _getTreeRef = (tree: ITree<ITestResultTreeData>) => {
        this._treeRef = tree;
    }

    private _closeTestResultGridPerfScenario() {
        if (
            this.props.artifactData.contributionSource === ContributionSource.Build ||
            this.props.artifactData.contributionSource === ContributionSource.Pipeline
        ) {
            if (this.state.endPerfMarker && !this._markedAsTTI) {
                if (this.state.errorMessage) {
                    this._performanceService.endScenario(
                        ViewContext[this.props.artifactData.viewContext],
                        this.props.artifactData.contributionSource,
                        true,
                        undefined,
                        { state: this._getArtifactStatus(), aborted: true, reason: this.state.errorMessage }
                    );
                } else {
                    // Checking if it's FastPageSwitch, to identify users coming from timeline view.
                    const isFPSSwitch = this._performanceService.isFastPageSwitchScenario();
                    this._performanceService.endScenario(
                        ViewContext[this.props.artifactData.viewContext],
                        this.props.artifactData.contributionSource,
                        true,
                        undefined,
                        { state: this._getArtifactStatus(), testsCount: this.state.totalResultsCount, isFPS: isFPSSwitch }
                    );
                }
                this._markedAsTTI = true;
            }
        }
    }

    private _getArtifactStatus(): string {
        if (this.props.artifactData.status == undefined) {
            return "undefined";
        }
        return ViewContextStatus[this.props.artifactData.status];
    }

    private _handleStoreChange = () => {
        this.setState({
            ...this.props.store.getState(),
            treeItemProvider: this.props.store.getTreeItemProvider()
        });
    };

    private _onSelectedItemsChanged = (selectedItems: ITestResultTreeData[]): void => {
        this.props.actionCreator.onSelectedItemsChanged(selectedItems);
        this._logTelemetryOnSelectionChanged(selectedItems);
    };

    private _onCellClick = (treeListItem: ITreeListItem<ITestResultTreeData>): void => {
        if (!treeListItem || !treeListItem.treeItem || !treeListItem.treeItem.item) {
            return;
        }
        this._lastSelectedItemOfGrid = document.activeElement;
        const item: ITestResultTreeData = treeListItem.treeItem.item;
        this._openRightPane(item);
    };

    private _openRightPane(item: ITestResultTreeData): void {
        if (this._shouldShowRightPane(item)) {
            this._performanceService.startScenario(TcmPerfScenarios.TestResultDetailsViewLoaded, false);
            this.props.onRowClick(item);

            // Firing window resize event on opening details panel
            // to adjust column width of Result Grid, because Grid rely on
            // it to resize column width.
            this._fireWindowResize();
        }
    }

    private _shouldShowRightPane(node: ITestResultTreeData): boolean {
        if (node.nodeType === TreeNodeType.showMore) {
            return false;
        }

        if (node.nodeType === TreeNodeType.group && !node.runId) {
            return false;
        }

        return true;
    }

    // Fire a window resize event.
    private _fireWindowResize() {
        const event = document.createEvent("Event");
        event.initEvent("resize", false, true);
        window.dispatchEvent(event);
    }

    private _logTelemetryOnSelectionChanged(selectedItems: ITestResultTreeData[]): void {
        if (selectedItems.length == 1) {
            const outcomeOfSelectedItem = selectedItems[0].outcome;
            Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, TelemetryConstants.featureTestTab_TestRowVisited, {
                TestResultOutcome: outcomeOfSelectedItem
            });
        }
        let countOfHierarchicalResultsSelected = 0;
        selectedItems.forEach(item => {
            countOfHierarchicalResultsSelected = item.subResultId ? countOfHierarchicalResultsSelected + 1 : countOfHierarchicalResultsSelected;
        });
        if (countOfHierarchicalResultsSelected > 0) {
            Telemetry.publishEvent(
                this.context.pageContext,
                this.props.artifactData,
                TelemetryConstants.featureResultsListView_SelectionOfHierarchicalResults,
                { Count: countOfHierarchicalResultsSelected }
            );
        }
    }

    private _onRowDidMount = (item: ITreeListItem<ITestResultTreeData>, index?: number) => {
        if (!item.treeItem || !item.treeItem.item) {
            return;
        }
        if (item.treeItem.item.nodeType === TreeNodeType.showMore) {
            this.props.store.onFetchMoreElements(item.treeItem.item.groupId);
        }
    };

    private _getLoadingSpinner(): JSX.Element {
        return (
            <Spinner
                ariaLabel={Resources.LoadingTestResultDetailsLabel}
                className="flex-grow"
                size={SpinnerSize.large}
                label={Resources.LoadingTestResultDetailsLabel}
            />
        );
    }

    private _getNoResultsFoundImage(): JSX.Element {
        const resourceFileName = getAssetLocation(this.context.pageContext, "ms.vss-test-web/common-content/no-results-image.png");
        const noResultsFoundMessage: string =
            this.state.fetchedResultsCount < this.state.totalResultsCount ? Resources.NoResultsForCurrentSetMessage : Resources.NoResultsMessage;
        const noResultsFoundSuggestionMessage: string =
            this.state.fetchedResultsCount < this.state.totalResultsCount
                ? Resources.NoResultsForCurrentSetSuggestionMessage
                : Resources.NoResultsSuggestionMessage;

        announce(noResultsFoundMessage);

        return (
            <div className={"no-results-div flex-column flex-center"}>
                <Image className={"no-results-image"} src={resourceFileName} alt={""} />
                <span className={"no-results-message"}>{noResultsFoundMessage}</span>
                <span className={"no-results-suggestion"}>{noResultsFoundSuggestionMessage}</span>
            </div>
        );
    }

    private _getNoFailedResultsImage(): JSX.Element {
        let resourceFileName: string, message: string, suggestionMessage: string;
        if (this.props.isNoConfigRuns) {
            resourceFileName = getAssetLocation(this.context.pageContext, "ms.vss-test-web/common-content/no-config-all-passed.svg");
            message = Resources.NoConfigAllPassedMessage;
            suggestionMessage = Resources.NoConfigAllPassedSuggestionMessage;
        } else {
            resourceFileName = getAssetLocation(this.context.pageContext, "ms.vss-test-web/common-content/no-failed-tests.svg");
            message = Resources.NoFailedResultsMessage;
            suggestionMessage = Resources.NoFailedResultsSuggestionMessage;
        }

        announce(message);

        return (
            <div className={"no-results-div flex-column flex-center"}>
                <Image className={"no-failed-tests-image"} src={resourceFileName} alt={""} />
                <span className={"no-results-message"}>{message}</span>
                <span className={"no-results-suggestion"}>
                    {suggestionMessage}
                    {this.props.isNoConfigRuns && (
                        <FPSLinkComponent
                            className="test-in-azure-pipeline-link"
                            href={"https://go.microsoft.com/fwlink/?linkid=2056113"}
                            telemetrySource={TelemetryConstants.featureTestTab_TestInAzurePipelineFwLinkClicked}
                        >
                            {Resources.LearnMoreText}
                        </FPSLinkComponent>
                    )}
                </span>
            </div>
        );
    }
    private _getWarningMessageBar(warningMessage: string): JSX.Element {
        return (
            <MessageBar className="test-results-grid-error-message-bar" severity={MessageBarSeverity.Error}>
                {warningMessage}
            </MessageBar>)
    }
    private _getFilterMessageBar(showingCount: number, totalCountToBeFetched: number): JSX.Element {
        const isFetchAllEnabled: boolean = isFeatureFlagEnabled(
            this.context.pageContext,
            FeatureFlagConstants.EnableFetchAllResultsUntilMegaBatchSize,
            false
        );
        if (isFetchAllEnabled) {
            if (this.state.totalResultsCount <= FilterSettings.getMegaResultsSize(this.context.pageContext)) {
                return (
                    <MessageBar className="load-more-tests-info" iconProps={{}}>
                        {this._getFetchAllMessageAndLoadingIcon()}
                    </MessageBar>
                );
            }
        }
        return (
            <MessageBar className="load-more-tests-info">
                {this._getLoadMoreMessage(showingCount, totalCountToBeFetched)}
                &nbsp;
                {this._getNextFilterMessageAndLink()}
            </MessageBar>
        );
    }

    private _onLoadMoreClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (event) {
            event.preventDefault();
        }

        this.props.actionCreator.onLoadMoreClick();
    };

    private _getLoadMoreMessage(showingResultsCount: number, totalResultsCount: number): JSX.Element {
        const isFilterCleared: boolean = Object.keys(this.state.filterState).length === 0;
        const message: string = format(
            isFilterCleared ? Resources.TestResultsFilterClearedMessage : Resources.LoadMoreMessage,
            showingResultsCount,
            totalResultsCount
        );
        return <span>{message}</span>;
    }

    private _getNextFilterMessageAndLink(): JSX.Element {
        const isFilterCleared: boolean = Object.keys(this.state.filterState).length === 0;

        return (
            <Link role="button" className="load-more-click" onClick={this._onLoadMoreClick} href="#">
                {isFilterCleared ? Resources.ShowMoreTestResults : Resources.FilterMoreTestResults}
            </Link>
        );
    }

    private _getFetchAllMessageAndLoadingIcon(): JSX.Element {
        const loadingIcon = <Spinner size={SpinnerSize.small} className="flex-grow" />;
        return (
            <span className="flex-row">
                {loadingIcon}&nbsp;
                {Resources.FetchingAll}
            </span>
        );
    }

    private _getColumns(columnsKeys: string[]): IColumn[] {
        const columns: IColumn[] = [];

        const column = this._columnEntry(ColumnIndices.Test);
        if (column) {
            columns.push(column);
        }

        columnsKeys.map(key => {
            const column = this._columnEntry(key);
            if (column) {
                columns.push(column);
            }
        });

        return columns;
    }

    private _columnEntry(key: string): IColumn | undefined {
        let column: IColumn;

        switch (key) {
            case ColumnIndices.Test:
                column = {
                    fieldName: Resources.ResultGridTitle_Test,
                    key: ColumnIndices.Test,
                    name: Resources.ResultGridTitle_Test,
                    ariaLabel: Resources.ResultGridTitle_Test,
                    minWidth: 180,
                    maxWidth: 700,
                    isResizable: true,
                    headerClassName: "testresults-column-header testresults-test",
                    className: "testresults-column-cell testresults-test",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: this._onRenderTestColumn
                };
                break;
            case ColumnIndices.Duration:
                column = {
                    fieldName: Resources.QueryColumnNameDuration,
                    key: ColumnIndices.Duration,
                    name: Resources.QueryColumnNameDuration,
                    ariaLabel: Resources.QueryColumnNameDuration,
                    minWidth: 80,
                    maxWidth: 200,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-duration",
                    className: "testresults-column-cell testresults-duration",
                    isSorted: true,
                    isSortedDescending: this.state.isSortedDescending,
                    columnActionsMode: this.state.loading ? ColumnActionsMode.disabled : ColumnActionsMode.clickable,
                    onColumnClick: ev => {
                        this._sortColumn(ev, column);
                    },
                    onRender: (item: ITreeItem<ITestResultTreeData>, index?: number, column?: IColumn) => {
                        return this._getElementWithTooltip(item.item ? item.item.duration : undefined);
                    }
                };
                break;
            case ColumnIndices.FailingRelease:
                column = {
                    fieldName: Resources.ResultGridHeader_FailingRelease,
                    key: ColumnIndices.FailingRelease,
                    name: Resources.ResultGridHeader_FailingRelease,
                    ariaLabel: Resources.ResultGridHeader_FailingRelease,
                    minWidth: 100,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-failingContext",
                    className: "testresults-column-cell testresults-failingContext",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: this._onRenderFailingArtifactColumn
                };
                break;
            case ColumnIndices.FailingBuild:
                column = {
                    fieldName: Resources.ResultGridHeader_FailingBuild,
                    key: ColumnIndices.FailingBuild,
                    name: Resources.ResultGridHeader_FailingBuild,
                    ariaLabel: Resources.ResultGridHeader_FailingBuild,
                    minWidth: 100,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-failingContext",
                    className: "testresults-column-cell testresults-failingContext",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: this._onRenderFailingArtifactColumn
                };
                break;
            case ColumnIndices.FailingSince:
                column = {
                    fieldName: Resources.ResultGridHeader_FailingSince,
                    key: ColumnIndices.FailingSince,
                    name: Resources.ResultGridHeader_FailingSince,
                    ariaLabel: Resources.ResultGridHeader_FailingSince,
                    minWidth: 90,
                    maxWidth: 280,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-failingSince",
                    className: "testresults-column-cell testresults-failingSince",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: (item: ITreeItem<ITestResultTreeData>, index?: number, column?: IColumn) => {
                        return this._getElementWithTooltip(item.item ? item.item.failingSince : undefined);
                    }
                };
                break;
            case ColumnIndices.Owner:
                column = {
                    fieldName: Resources.ResultGridHeader_Owner,
                    key: ColumnIndices.Owner,
                    name: Resources.ResultGridHeader_Owner,
                    ariaLabel: Resources.ResultGridHeader_Owner,
                    minWidth: 120,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-owner",
                    className: "testresults-column-cell testresults-owner",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: (item: ITreeItem<ITestResultTreeData>, index?: number, column?: IColumn) => {
                        return this._getElementWithTooltip(item.item ? item.item.owner : undefined);
                    }
                };
                break;
            case ColumnIndices.DateStarted:
                column = {
                    fieldName: Resources.ResultGridHeader_DateStarted,
                    key: ColumnIndices.DateStarted,
                    name: Resources.ResultGridHeader_DateStarted,
                    ariaLabel: Resources.ResultGridHeader_DateStarted,
                    minWidth: 150,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-date-started",
                    className: "testresults-column-cell testresults-date-started",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: (item: ITreeItem<ITestResultTreeData>, index?: number, column?: IColumn) => {
                        return this._getElementWithTooltip(item.item ? item.item.dateStarted : undefined);
                    }
                };
                break;
            case ColumnIndices.DateCompleted:
                column = {
                    fieldName: Resources.ResultGridHeader_DateCompleted,
                    key: ColumnIndices.DateCompleted,
                    name: Resources.ResultGridHeader_DateCompleted,
                    ariaLabel: Resources.ResultGridHeader_DateCompleted,
                    minWidth: 150,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-date-completed",
                    className: "testresults-column-cell testresults-date-completed",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: (item: ITreeItem<ITestResultTreeData>, index?: number, column?: IColumn) => {
                        return this._getElementWithTooltip(item.item ? item.item.dateCompleted : undefined);
                    }
                };
                break;
            case ColumnIndices.Tags:
                column = {
                    fieldName: Resources.ResultGridHeader_Tags,
                    key: ColumnIndices.Tags,
                    name: Resources.ResultGridHeader_Tags,
                    ariaLabel: Resources.ResultGridHeader_Tags,
                    minWidth: 150,
                    isResizable: true,
                    isCollapsable: true,
                    headerClassName: "testresults-column-header testresults-tags",
                    className: "testresults-column-cell testresults-tags",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: this._onRenderTagsColumn
                };
                break;
            default:
                return undefined;
        }

        return column;
    }

    private _sortColumn = (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        this.props.actionCreator.toggleSort();
        if (column.isSortedDescending) {
            announce(localeFormat(Resources.DetailedListSortedAsc, Resources.QueryColumnNameDuration), true, 500);
        } else {
            announce(localeFormat(Resources.DetailedListSortedDesc, Resources.QueryColumnNameDuration), true, 500);
        }

        Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, TelemetryConstants.featureTestTab_DurationSorted, {
            [TelemetryConstants.eventClicked]: 1
        });
    };

    private _onRenderTagsColumn = (treeItem: ITreeItem<ITestResultTreeData>, index: number, column?: IColumn): JSX.Element => {
        if (!treeItem.item || !treeItem.item.tags || treeItem.item.tags.length === 0) {
            return <div />;
        }

        return (
            <WrappedComponent
                dependencies={["ms.vss-test-web.tagsList"]}
                wrappedType="test-tags-list"
                context={this.props.artifactData}
                tags={treeItem.item.tags}
            />
        );
    };

    private _onRenderTestColumn = (treeItem: ITreeItem<ITestResultTreeData>, index: number, column?: IColumn): JSX.Element => {
        if (!treeItem.item) {
            return <div />;
        }

        const item = treeItem.item;
        const outcomeConfidenceDiv = this._getOutcomeConfidenceElement(item);
        const countDiv = this._getCountElement(item);
        const tooltipContent = item.outcome ? getFriendlyName(item.outcome) : "";
        if (item.nodeType === TreeNodeType.showMore) {
            return <Spinner size={SpinnerSize.small} ariaLabel={Resources.LoadingMessage} />;
        }

        return (
            <div className="test-results-cell flex-row flex-center">
                {this._getOutcomeIconElement(item, tooltipContent)}
                <Tooltip text={item.test}>
                    <span
                        className="clickable-text"
                        onClick={() => {
                            this._lastSelectedItemOfGrid = document.activeElement;
                            this._openRightPane(item);
                        }}
                    >
                        {item.test}
                    </span>
                </Tooltip>
                {outcomeConfidenceDiv}
                {countDiv}
            </div>
        );
    };

    private _onRenderFailingArtifactColumn = (item: ITreeItem<ITestResultTreeData>, index: number, column?: IColumn): JSX.Element | undefined => {
        if (!item.item || !item.item.failingContextName || !item.item.failingContextId) {
            return undefined;
        }
        let artifactUrl = "";
        let telemetryEvent = "";
        if (this.props.artifactData.viewContext === ViewContext.Build) {
            telemetryEvent = TelemetryConstants.featureTestTab_BuildLinkClicked;
            artifactUrl = this._getBuildUrl(item.item.failingContextId.toString());
        } else {
            TelemetryConstants.featureTestTab_ReleaseLinkClicked;
            artifactUrl = this._getReleaseUrl(item.item.failingContextId.toString());
        }

        return (
            <span className="text-ellipsis">
                <Tooltip text={item.item.failingContextName}>
                    <span>
                        {item.item.isCurrentArtifact
                            ? item.item.failingContextName
                            : this._getFailingArtifactLink(artifactUrl, item.item.failingContextName, telemetryEvent)}
                    </span>
                </Tooltip>
            </span>
        );
    };

    private _getFailingArtifactLink(artifactUrl: string, failingContextName: string, telemetryEvent: string): JSX.Element {
        return (
            <FPSLinkComponent
                href={artifactUrl}
                className={"artifact-url " + this.dontOpenDetailsPaneCssClass}
                onClick={e => {
                    Telemetry.publishEvent(this.context.pageContext, this._artifactData, telemetryEvent, {
                        [TelemetryConstants.eventClicked]: 1
                    });
                }}
                rel={"nofollow noopener noreferrer"}
                telemetrySource={telemetryEvent}
                telemetryProperties={{
                    [TelemetryConstants.eventClicked]: 1
                }}
            >
                {failingContextName}
            </FPSLinkComponent>
        );
    }

    private _getOutcomeIconElement(resultData: ITestResultTreeData, tooltipText: string): JSX.Element | undefined {
        if ((resultData.runOutcome as number) === -1 && !resultData.isTestCaseRow) {
            return undefined;
        }
        let iconDetails: IconDetails;
        let ariaLabel: string;
        if (resultData.outcome) {
            iconDetails = getIconDetails(resultData.outcome);
            ariaLabel = getFriendlyName(resultData.outcome);
        } else if (resultData.runOutcome) {
            iconDetails = getIconDetails(resultData.runOutcome);
            ariaLabel = getFriendlyName(resultData.runOutcome);
        } else {
            return undefined;
        }
        return (
            <Link role="tooltip" target={"_blank"} className={"outcome-icon"} tooltipProps={{ text: tooltipText }}>
                <div className="testresult-outcome-icon">
                    <Icon iconName={iconDetails.iconName} ariaLabel={ariaLabel} className={iconDetails.className} />
                </div>
            </Link>
        );
    }

    private _getBuildUrl(buildId: string) {
        const newBuildHubRoute = "ms.vss-build-web.ci-results-hub-route";
        const tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["buildId"] = buildId;
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";

        return routeUrl(this.context.pageContext, newBuildHubRoute, routeValues);
    }

    private _getReleaseUrl(releaseId: string) {
        const newReleaseHubRoute = "ms.vss-releaseManagement-web.cd-release-progress-default-route";
        const tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["releaseId"] = releaseId;
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";

        return routeUrl(this.context.pageContext, newReleaseHubRoute, routeValues);
    }

    private _getOutcomeConfidenceElement(resultData: ITestResultTreeData): JSX.Element | undefined {
        if (resultData.isTestCaseRow && (resultData.isNewFailure || resultData.isUnreliable || resultData.flakyState)) {
            return (
                <div className="outcome-confidence flex-row flex-center">
                    {resultData.isNewFailure && <span className="new-failure-indicator">{Resources.NewFailureIndicator}</span>}
                    {resultData.isNewFailure && resultData.isUnreliable && (
                        <span className="result-indicator-separator">{Resources.TestIndicatorSeparator}</span>
                    )}
                    {resultData.isUnreliable && (
                        <span
                            className="flaky-result-indicator"
                            style={{ textDecoration: resultData.flakyState === TestResultFlakyState.Unmarked ? "line-through" : "none" }}
                        >
                            {Resources.FlakyResultIndicator}
                        </span>
                    )}
                    {(resultData.isNewFailure || resultData.isUnreliable) && resultData.flakyState === TestResultFlakyState.Marked && (
                        <span className="result-indicator-separator">{Resources.TestIndicatorSeparator}</span>
                    )}
                    {resultData.flakyState === TestResultFlakyState.Marked && (
                        <span className="marked-flaky-result-indicator">{Resources.MarkedFlakyResultIndication}</span>
                    )}
                </div>
            );
        } else {
            return undefined;
        }
    }

    private _getCountElement(resultData: ITestResultTreeData): JSX.Element | undefined {
        if (resultData.isTestCaseRow) {
            return undefined;
        }

        let countString: string = format("({0}/{1})", resultData.filteredTestsCount, resultData.totalTestsCount);

        // Instead of showing incomplete counts in totaltestsCount we will show only filteredTestsCount
        if (
            !this.state.hasMegaFetchStarted &&
            this.state.groupByField !== TestResultsGroupPivots.Group_By_Test_Run &&
            this.state.groupByField !== TestResultsGroupPivots.Group_By_Requirement &&
            this.state.groupByField !== TestResultsGroupPivots.Group_By_Test_Suite
        ) {
            countString = format("({0})", resultData.filteredTestsCount);
        }

        return <span className="testresult-group-count"> {countString} </span>;
    }

    private _getElementWithTooltip(text: string | undefined): JSX.Element {
        return (
            <Tooltip overflowOnly={true} text={text}>
                <span className="flex flex-grow text-ellipsis"> {text} </span>
            </Tooltip>
        );
    }
}
